import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { twoFactorStatusQuery } from '@/lib/two-factor';
import { TwoFactorDisableDialog } from './two-factor-disable-dialog';
import { TwoFactorRegenerateDialog } from './two-factor-regenerate-dialog';
import { TwoFactorSetupDialog } from './two-factor-setup-dialog';

interface TwoFactorSectionProps {
  /** True when /account was reached via `?setup=2fa` (e.g. from the
   *  Required2faBanner). Auto-opens the setup dialog once, then the
   *  param gets stripped from the URL so closing+refreshing doesn't
   *  re-trigger. No-op when the user already has 2FA enabled. */
  autoOpenSetup?: boolean;
}

export function TwoFactorSection({ autoOpenSetup = false }: TwoFactorSectionProps) {
  const status = useQuery(twoFactorStatusQuery);
  const navigate = useNavigate();

  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const enabled = status.data?.enabled ?? false;
  const remaining = status.data?.remainingBackupCodes ?? 0;

  // Auto-open guard — fire exactly once per mount. Without this, if
  // react-query refetches twoFactorStatusQuery and `enabled` flips
  // false→true→false (unlikely but possible during disable flow), the
  // effect would re-open the dialog after the user closed it.
  const autoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (autoOpenFiredRef.current) return;
    if (!autoOpenSetup) return;
    // Wait for status to load — auto-opening before we know whether
    // the user is already enrolled would briefly flash the dialog at
    // a user who doesn't need it.
    if (status.isLoading) return;
    autoOpenFiredRef.current = true;
    if (!enabled) setSetupOpen(true);
    // Strip the deep-link param so refresh / back-button doesn't
    // reopen. `replace: true` avoids polluting browser history with a
    // "/account?setup=2fa" → "/account" step.
    void navigate({ to: '/account', search: {}, replace: true });
  }, [autoOpenSetup, status.isLoading, enabled, navigate]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-4">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            enabled
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
          }`}
        >
          {enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">
                การยืนยันตัวตน 2 ขั้นตอน (2FA)
              </h3>
              <p className="mt-0.5 text-sm text-slate-500">
                {status.isLoading
                  ? 'กำลังโหลด...'
                  : enabled
                    ? `เปิดใช้งานแล้ว · เหลือ ${remaining} backup codes`
                    : 'ยังไม่ได้เปิดใช้งาน — แนะนำให้เปิดเพื่อความปลอดภัยของบัญชี'}
              </p>
            </div>
            {!status.isLoading && (
              <>
                {enabled ? (
                  <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)}>
                    ปิดใช้งาน
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setSetupOpen(true)}>
                    เปิดใช้งาน
                  </Button>
                )}
              </>
            )}
          </div>

          {enabled && (
            <div className="mt-4 space-y-3">
              {remaining <= 3 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    เหลือ backup codes แค่ {remaining} รหัส — แนะนำให้สร้างชุดใหม่
                  </AlertDescription>
                </Alert>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRegenOpen(true)}
                className="gap-2"
              >
                <KeyRound className="h-3.5 w-3.5" />
                สร้าง Backup codes ชุดใหม่
              </Button>
            </div>
          )}
        </div>
      </div>

      <TwoFactorSetupDialog open={setupOpen} onOpenChange={setSetupOpen} />
      <TwoFactorDisableDialog open={disableOpen} onOpenChange={setDisableOpen} />
      <TwoFactorRegenerateDialog open={regenOpen} onOpenChange={setRegenOpen} />
    </section>
  );
}
